import { Fragment } from 'react';

import styled from 'styled-components';

import { theme } from '@styles/theme';
import { transitionStandard } from '@utils/transitions';

export interface StepperProps {
  activeIndex: number;
  items: Array<{ label: string }>;
  showCompleted?: boolean;
}

export function Stepper({ items, activeIndex, showCompleted = false }: StepperProps) {
  return (
    <Steps aria-label="Steps">
      {items.map((item, index) => (
        <Fragment key={index + item.label}>
          <Step
            item={item}
            isCurrent={index === activeIndex}
            displayIndex={index + 1}
            isCompleted={showCompleted && index < activeIndex}
          />
          {index < items.length - 1 && <StepSeparator />}
        </Fragment>
      ))}
    </Steps>
  );
}

function Step({
  item,
  isCurrent,
  displayIndex,
  isCompleted,
}: {
  item: StepperProps['items'][number];
  isCurrent: boolean;
  displayIndex: number;
  isCompleted: boolean;
}) {
  return (
    <Item
      data-active={isCurrent}
      data-completed={isCompleted}
      aria-current={isCurrent ? 'step' : undefined}
    >
      <div>{displayIndex}</div>
      <div>{item.label}</div>
    </Item>
  );
}

const Steps = styled.ol({
  height: '1.5rem',
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  alignItems: 'center',
});

const Item = styled.li({
  font: theme.font.bold14,
  color: theme.color.textHelper,
  transition: transitionStandard('color'),
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',

  '> div:first-child': {
    display: 'flex',
    flexShrink: 0,
    font: theme.font.semibold12,
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.color.white100,
    marginRight: '0.5rem',
    height: '1.5rem',
    width: '1.5rem',
    borderRadius: '50%',
    backgroundColor: theme.color.purple50,
  },

  '&[data-active="true"]': {
    color: theme.color.elementBlue100,
    '> div:first-child': {
      backgroundColor: theme.color.elementBlue100,
    },
  },

  '&[data-completed="true"]': {
    color: theme.color.green100,
    '> div:first-child': {
      backgroundColor: theme.color.elementGreen100,
    },
  },
});

const StepSeparator = styled.li.attrs({
  role: 'separator',
})({
  height: '0.0625rem',
  width: '100%',
  maxWidth: '2rem',
  backgroundColor: theme.color.uiTrans10,
  margin: '0 1rem',
});
